import { useContext, useMemo } from 'react';

import { DownloadOutlined } from '@ant-design/icons';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { ColSize, ContentSection, RowSize } from '../../../../../../../shared/components';

import {
  Column,
  _renderColumns,
} from '../../../../../../production/InsurancePolicyDetail/utils/renderSections';
import {
  displayNotImplementedMessage,
  formattedPrice,
} from '../../../../../../../shared/utils';
import { QUOTATION_BUTTON } from '../../interfaces';
import { QuotationContextData } from '../../../../../../../shared/interfaces/services/quote-pdf-service';
import { BranchDocumentsURL, BudgetType } from '../../../utils/quotation-documents.enum';
import { GenericDataFormContext, StepOneFormContext } from '../../contexts';
import { WizardDataFormContext } from '../../../AP/Wizard/contexts/WizardDataForm';
import { StepThreeFormContext } from '../../../AP/Steps/StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../AP/Steps/StepTwo/contexts/StepTwoForm';
import { getQuoteDocumentPDF, transformToPdfQuote } from '../../../utils';

interface QuotationResponseType {
  totalSumaAsegurada: string;
  primaOperacion: string;
  primaPremioOperacion: {
    importeComision: string;
  };
  premioOperacion: string;
}

interface SummarySectionProps<T extends QuotationResponseType> {
  labelButton: string;
  quotationResponse: T;
  reQuotationResponse?: T;
}



export const SummarySection = <T extends QuotationResponseType>({
  labelButton,
  quotationResponse,
  reQuotationResponse,
}: SummarySectionProps<T>) => {

  const { stepOneForm, clientSelected } = useContext(StepOneFormContext);
  const { stepTwoForm, propertyDataForm, apActiviesListOptions, productsList } = useContext(StepTwoFormContext);
  const { activitiesForm, paymentMethodsForm, } = useContext(StepThreeFormContext);
  const { quotationForm, ageRangeList, relationshipList, } = useContext(WizardDataFormContext);
  const { provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, allCoveragesPerPlan, paymentMethodsList } = useContext(GenericDataFormContext);

  const ctx: QuotationContextData = {
    quotationForm,
    clientSelected,
    stepOneForm,
    stepTwoForm, propertyDataForm, apActiviesListOptions, productsList,
    activitiesForm, paymentMethodsForm,
    quotationResponse,  ageRangeList, relationshipList, paymentMethodsList,
    provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, allCoveragesPerPlan,
    branchDocumentsURL: BranchDocumentsURL.AP
  };

  const summaryInfo: Array<Column[]> = useMemo(
    () => [
      [
        {
          label: 'Total suma asegurada:',
          value:
            labelButton === QUOTATION_BUTTON.RECOTIZAR ? (
              labelButton
            ) : (
              <b>
                {reQuotationResponse
                  ? `$ ${formattedPrice(
                      reQuotationResponse.totalSumaAsegurada
                    )}`
                  : `$ ${formattedPrice(
                      quotationResponse?.totalSumaAsegurada
                    )}`}
              </b>
            ),
          columnSize: {
            sm: { span: 12 },
            xs: { span: 12 },
            md: { span: 10 },
            lg: { span: 7 },
          },
        },
        {
          label: 'Prima técnica:',
          value:
            labelButton === QUOTATION_BUTTON.RECOTIZAR
              ? labelButton
              : `$ ${formattedPrice(
                  reQuotationResponse
                    ? reQuotationResponse.primaOperacion
                    : quotationResponse?.primaOperacion
                )}`,
          columnSize: {
            sm: { span: 12 },
            xs: { span: 12 },
            md: { span: 7 },
            lg: { span: 7 },
          },
        },
        {
          label: 'Comisión:',
          value:
            labelButton === QUOTATION_BUTTON.RECOTIZAR
              ? labelButton
              : `$ ${formattedPrice(
                  reQuotationResponse
                    ? reQuotationResponse.primaPremioOperacion.importeComision
                    : quotationResponse?.primaPremioOperacion?.importeComision
                )}`,
          columnSize: {
            sm: { span: 12 },
            xs: { span: 12 },
            md: { span: 6 },
            lg: { span: 7 },
          },
        },
        {
          label: 'Premio:',
          value:
            labelButton === QUOTATION_BUTTON.RECOTIZAR
              ? labelButton
              : `$ ${formattedPrice(
                  reQuotationResponse
                    ? reQuotationResponse.premioOperacion
                    : quotationResponse?.premioOperacion
                )}`,
          columnSize: {
            sm: { span: 12 },
            xs: { span: 12 },
            md: { span: 10 },
            lg: { span: 7 },
          },
        },
        {
          label: 'Acciones',
          value:
            labelButton === QUOTATION_BUTTON.RECOTIZAR ? (
              labelButton
            ) : (
              <>
                <ActionButton
                  className='btn-accent-outline-barbuss'
                  type='secondary'
                  label='Productor'
                  onClick={() => getQuoteDocumentPDF(transformToPdfQuote(ctx, BudgetType.PRODUCTOR), BranchDocumentsURL.AP)}
                  icon={<DownloadOutlined />}
                />
                <ActionButton
                  className='btn-accent-outline-barbuss'
                  type='secondary'
                  style={{ marginLeft: '1rem' }}
                  color='secondaryDefault'
                  label='Cliente'
                  onClick={() => getQuoteDocumentPDF(transformToPdfQuote(ctx, BudgetType.CLIENTE), BranchDocumentsURL.AP)}
                  icon={<DownloadOutlined />}
                />
              </>
            ),
          columnSize: {
            sm: { span: 24 },
            xs: { span: 24 },
            md: { span: 14 },
            lg: { span: 10 },
          },
        },
      ],
    ],
    [labelButton, quotationResponse, reQuotationResponse]
  );

  return (
    <ContentSection level='h3' title='Resumen'>
      <RowSize>
        <ColSize size={100}>
          {summaryInfo.map((insured, index) => (
            <RowSize key={`${insured[0].label}-${index}`}>
              {_renderColumns(insured)}
            </RowSize>
          ))}
        </ColSize>
      </RowSize>
    </ContentSection>
  );
};
